/**
 * Client hooks for the Project Computers control plane. sphinx has no
 * computer control plane (it was a Convex surface on the mcpjam backend): the
 * status/usage queries resolve to "no computer" (`null`) and the lifecycle
 * mutations reject. The data-plane config is a constant for the same reason —
 * the fork probed `GET /api/web/computers/config`, a route sphinx never
 * mounted, so every Evaluate page load spent a 404 to reach its own fail-open
 * fallback. That fallback IS the answer here, stated directly.
 */

const computersUnavailable = async (..._args: unknown[]): Promise<never> => {
  throw new Error("Computers are not available in sphinx.");
};

/** Provider-side lifecycle status surfaced by `getComputerStatus`. */
export type ComputerStatus =
  | "requested"
  | "provisioning"
  | "ready"
  | "waking"
  | "hibernating"
  | "deleting"
  | "deleted"
  | "error";

/** Why a computer is currently asleep, when the backend knows (COMP-7). */
export type ComputerHibernatedReason = "idle" | "billing";

export interface ComputerView {
  computerId: string;
  status: ComputerStatus;
  provider: string;
  lastError?: string;
  provisionedAt?: number;
  lastActiveAt?: number;
  /** The custom environment this computer boots from, if any (absent ⇒ base
   * image). See `computerEnvironments` / the Image picker. */
  environmentId?: string;
  /**
   * Why the machine is currently asleep, when known (COMP-7). Only meaningful
   * while `status === "hibernating"`. `"billing"` ⇒ paused because the compute
   * allowance ran out and the wallet couldn't cover the overage — waking it
   * would just bounce, so the UI shows the remedy instead. Absent (older
   * backends, or a clean idle sleep) ⇒ treat as inactivity.
   */
  hibernatedReason?: ComputerHibernatedReason;
}

export interface TerminalTokenResult {
  token: string;
  expiresAt: number;
  computerId: string;
  status: ComputerStatus;
}

/**
 * Org-level awake-time meter surfaced by `getComputerUsage`: settled awake
 * time for the current UTC month against the plan's free allowance, plus the
 * posted credits-per-hour rate. `allowanceMs: null` means unlimited hours.
 * `mode` is the deployment's billing posture — `off` means the backend isn't
 * metering and the UI should hide the meter.
 */
export interface ComputerUsageView {
  mode: "off" | "shadow" | "enforce";
  creditsPerHour: number;
  windowStartAt: number;
  resetsAt: number;
  awakeMs: number;
  allowanceMs: number | null;
  billedCredits: number;
  forgivenCredits: number;
  /**
   * COMP-7: true when the org has burned >= 80% of a finite allowance AND the
   * wallet can't absorb the overage that begins once it's exhausted — i.e. the
   * computer will pause for billing when the hours run out. Always false for
   * unlimited (enterprise) orgs. Optional so a backend predating this field
   * (or an older client) degrades to "no warning".
   */
  billingPauseWarning?: boolean;
}

/**
 * Live status of the caller's computer for a project, or `null` when they
 * have none (or it was deleted). `undefined` while the query loads or when
 * `projectId` is absent.
 */
export function useComputerStatus(
  projectId: string | null
): ComputerView | null | undefined {
  return projectId ? null : undefined;
}

/**
 * The org's computer-time meter for a project, or `null` when the backend
 * can't resolve it. `undefined` while loading or when `projectId` is absent.
 * Throws during render (like any Convex query error) against a backend that
 * predates `getComputerUsage` — mount it behind an error boundary.
 */
export function useComputerUsage(
  projectId: string | null
): ComputerUsageView | null | undefined {
  return projectId ? null : undefined;
}

/** Reserve (provision-on-first-use / wake) the caller's computer. */
export function useReserveComputer(): (args: {
  projectId: string;
}) => Promise<ComputerView> {
  return computersUnavailable;
}

/** Tear down the caller's computer for a project. */
export function useDeleteComputer(): (args: {
  projectId: string;
}) => Promise<{ deleted: boolean }> {
  return computersUnavailable;
}

/**
 * Manually hibernate the caller's `ready` computer (non-destructive; state is
 * preserved and the box auto-resumes on next use). Returns `{ hibernated:false }`
 * when there's no live machine to pause.
 */
export function useHibernateComputer(): (args: {
  projectId: string;
}) => Promise<{ hibernated: boolean }> {
  return computersUnavailable;
}

/**
 * Mint a short-lived (~60s) terminal token authorizing a WebSocket to the
 * inspector server's terminal bridge. An ACTION (needs crypto.subtle).
 */
export function useMintTerminalToken(): (args: {
  projectId: string;
}) => Promise<TerminalTokenResult> {
  return computersUnavailable;
}

/**
 * Which data plane serves this inspector: itself (`localConfigured` — it holds
 * the vendor key + secrets) or a deployed one (`remoteDataPlaneUrl`). Neither ⇒
 * computers are unavailable here and the UI should say so instead of offering a
 * terminal that can't connect.
 */
export interface ComputerEnginesConfig {
  local: {
    available: boolean;
    /** Bash may work while the terminal doesn't (node-pty failed to load). */
    terminalAvailable: boolean;
    /** Tilde display root ("~/.mcpjam/computer") — render `${root}/<projectId>`. */
    workspaceDisplayRoot: string | null;
    reason?: string;
  };
  cloud: { available: boolean };
}

export interface ComputersDataPlaneConfig {
  localConfigured: boolean;
  remoteDataPlaneUrl: string | null;
  /**
   * Can this inspector EXECUTE in ephemeral (eval/swarm/scenario) sandboxes?
   * Distinct from personal-computer availability: `remoteDataPlaneUrl`
   * delegates only personal bash/terminal, so a remote-only inspector can
   * drive a personal Computer yet cannot run a single disposable-sandbox
   * command. Read from the server's `capabilities.ephemeralCloudAvailable`
   * when present; older servers omit it and the derivation falls back to
   * `localConfigured` (creds present ⇔ ephemeral exec works).
   */
  ephemeralCloudAvailable?: boolean;
  /**
   * Per-engine availability. Always present POST-PARSE: servers that predate
   * the field get a derived block — local unavailable (an old server has no
   * local engine to offer), cloud from the legacy pair — so consumers never
   * branch on absence.
   */
  engines: ComputerEnginesConfig;
  /** Server's suggested engine; `null` when NO engine can serve here. */
  defaultEngine: "local" | "cloud" | null;
}

/**
 * sphinx's fixed answer. The fork read this from the server; the route it read
 * is gone, and its own fail-open fallback ("assume a local data plane, never a
 * phantom local machine engine") is the truth here: the E2B sandbox provider
 * this server keeps serves the cloud engine, and there is no personal machine
 * engine at all.
 */
const DATA_PLANE_CONFIG: ComputersDataPlaneConfig = {
  localConfigured: true,
  remoteDataPlaneUrl: null,
  engines: {
    local: {
      available: false,
      terminalAvailable: false,
      workspaceDisplayRoot: null,
    },
    cloud: { available: true },
  },
  defaultEngine: "cloud",
};

/**
 * Preflight bit for the cloud-only surfaces (evals): `false` means a run that
 * needs a disposable sandbox WILL fail to execute commands on this inspector,
 * and the UI should say so before launch instead of after.
 */
export function useEphemeralCloudAvailable(): boolean | undefined {
  return (
    DATA_PLANE_CONFIG.ephemeralCloudAvailable ??
    DATA_PLANE_CONFIG.localConfigured
  );
}

/** Never `undefined` any more — there is nothing to load. */
export function useComputersDataPlaneConfig():
  | ComputersDataPlaneConfig
  | undefined {
  return DATA_PLANE_CONFIG;
}
